import { dayKey } from '@pos/shared';
/** Sales history, customers, store credit and expenses. */
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useI18n, useT } from '../../core/i18n/index.js';
import { useSettings } from '../../core/state/settings.js';
import { useAuth } from '../../core/state/auth.js';
import { useToast } from '../../core/state/toast.js';
import { api } from '../../core/api.js';
import { printReceipt, printDocument, escapeHtml, barcodeSvg, type ReceiptData } from '../../core/print.js';
import { paymentLabel } from '../../core/format.js';
import { Button, Confirm, Empty, Field, Loading, Modal, MoneyInput, Pill } from '../../core/ui/ui.js';
import { RangePicker, useDateRange, Barcode } from '../../core/ui/bits.js';
import { TakePayment } from './Owed.js';

// ---------------------------------------------------------------------------
// Sales history
// ---------------------------------------------------------------------------

export function Sales() {
  const t = useT();
  const { money, date, dateTime } = useSettings();
  const { can } = useAuth();
  const toast = useToast();
  const { range, setRange } = useDateRange('month');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(
        await api<Record<string, unknown>[]>(
          `/sales?from=${range.from}&to=${range.to}&q=${encodeURIComponent(q)}`,
        ),
      );
    } catch {
      toast.warn(t('net.needOnline'));
    } finally {
      setLoading(false);
    }
  }, [range, q, t, toast]);

  useEffect(() => {
    const timer = setTimeout(() => void load(), 200);
    return () => clearTimeout(timer);
  }, [load]);

  return (
    <div className="col">
      <div className="row wrap">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('sales.searchPlaceholder')} aria-label={t('sales.searchPlaceholder')}
          style={{ maxWidth: 360 }}
          data-scan-target="true"
        />
        <RangePicker range={range} onChange={setRange} />
      </div>

      {loading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty icon="📜" title={t('sales.empty')} />
      ) : (
        <div className="card flush">
          <div className="table-wrap">
            <table className="cards">
              <thead>
                <tr>
                  <th>{t('receipt.no')}</th>
                  <th>{t('common.date')}</th>
                  <th>{t('common.cashier')}</th>
                  <th>{t('common.customer')}</th>
                  <th className="num">{t('common.items')}</th>
                  <th className="num">{t('common.total')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  // The list runs newest first; when the day changes, say so and
                  // total the day before moving on - the question at this screen
                  // is nearly always "how did Tuesday do".
                  const day = dayKey(String(r.created_at));
                  const firstOfDay = i === 0 || dayKey(String(rows[i - 1]!.created_at)) !== day;
                  const dayRows = firstOfDay ? rows.filter((x) => dayKey(String(x.created_at)) === day) : [];
                  const methods = String(r.methods ?? '').split(',').filter(Boolean);
                  return (
                  <Fragment key={String(r.id)}>
                  {firstOfDay ? (
                    <tr className="day-row">
                      <td colSpan={4} className="strong">{date(String(r.created_at))}</td>
                      <td className="num muted">{t('sales.onDay', { n: dayRows.length })}</td>
                      <td className="num tnum strong">{money(dayRows.reduce((a, x) => a + Number(x.total), 0))}</td>
                    </tr>
                  ) : null}
                  <tr style={{ cursor: 'pointer' }} onClick={() => setOpen(String(r.id))}>
                    <td className="strong" data-label={t('receipt.no')}>
                      {String(r.receipt_no)}
                      {String(r.status) === 'voided' ? <> <Pill kind="danger">{t('sales.voided')}</Pill></> : null}
                      {String(r.kind) !== 'sale' ? (
                        <> <Pill kind="info">{String(r.kind) === 'exchange' ? t('sales.kindExchange') : t('sales.kindReturn')}</Pill></>
                      ) : null}
                    </td>
                    <td data-label={t('common.date')}>{dateTime(String(r.created_at))}</td>
                    <td data-label={t('common.cashier')}>{String(r.user_name ?? '')}</td>
                    <td data-label={t('common.customer')}>{String(r.customer_name ?? '')}</td>
                    <td className="num" data-label={t('common.items')}>{Number(r.item_count)}</td>
                    <td className="num tnum strong" data-label={t('common.total')}>
                      {money(Number(r.total))}
                      {methods.length ? (
                        <span className="pay-pills">
                          {methods.map((m) => (
                            <Pill key={m} kind={m === 'tab' ? 'warn' : m === 'credit' || m === 'gift' ? 'info' : undefined}>
                              {paymentLabel(m, t)}
                            </Pill>
                          ))}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {open ? (
        <SaleDetail
          id={open}
          canVoid={can('sales.void')}
          onClose={() => setOpen(null)}
          onChanged={() => {
            setOpen(null);
            void load();
          }}
        />
      ) : null}
    </div>
  );
}

function SaleDetail({
  id,
  canVoid,
  onClose,
  onChanged,
}: {
  id: string;
  canVoid: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const t = useT();
  const { lang } = useI18n();
  const { settings, money, dateTime } = useSettings();
  const toast = useToast();
  const [sale, setSale] = useState<Record<string, unknown> | null>(null);
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    void api<Record<string, unknown>>(`/sales/${id}`).then(setSale).catch(() => undefined);
  }, [id]);

  if (!sale) return <Modal title={t('common.loading')} onClose={onClose}><Loading /></Modal>;

  const lines = (sale.lines as Record<string, unknown>[]) ?? [];
  const payments = (sale.payments as Record<string, unknown>[]) ?? [];

  const receipt: ReceiptData = {
    receiptNo: String(sale.receipt_no),
    createdAt: String(sale.created_at),
    cashierName: String(sale.user_name ?? ''),
    lines: lines.map((l) => ({
      nameEn: String(l.name_en),
      nameAr: String(l.name_ar),
      size: String(l.size),
      colorEn: String(l.color_en ?? ''),
      colorAr: String(l.color_ar ?? ''),
      qty: Number(l.qty),
      unitPrice: Number(l.unit_price),
      net: Number(l.net),
      promo: Number(l.promo_discount) > 0
        ? { nameEn: String(l.promo_name_en ?? ''), nameAr: String(l.promo_name_ar ?? ''), amount: Number(l.promo_discount) }
        : null,
    })),
    gross: Number(sale.gross),
    discountTotal: Number(sale.discount_total),
    promoTotal: lines.reduce((s, l) => s + (Number(l.qty) > 0 ? Number(l.promo_discount ?? 0) : 0), 0),
    total: Number(sale.total),
    rounding: Number(sale.rounding),
    payable: Number(sale.payable),
    vatAmount: Number(sale.vat_amount),
    netAmount: Number(sale.net_amount),
    vatRate: Number(sale.vat_rate),
    changeIls: Number(sale.change_ils),
    payments: payments.map((p) => ({
      // The method, not a label: the receipt names it in the SHOP's language.
      method: String(p.method),
      methodLabel: p.method_label ? String(p.method_label) : null,
      currency: String(p.currency),
      amount: Number(p.amount),
      amountIls: Number(p.amount_ils),
      rate: Number(p.rate_micros) / 1_000_000,
    })),
  };

  return (
    <Modal
      title={`${t('common.receipt')} ${sale.receipt_no}`}
      wide
      onClose={onClose}
      footer={
        <>
          {canVoid && String(sale.status) !== 'voided' ? (
            <Button variant="danger" onClick={() => setVoiding(true)}>{t('sales.void')}</Button>
          ) : null}
          <div className="spacer" />
          <Button onClick={() => void printReceipt(receipt, settings, true)}>{t('receipt.gift')}</Button>
          <Button variant="primary" onClick={() => void printReceipt(receipt, settings)}>{t('receipt.reprint')}</Button>
        </>
      }
    >
      <div className="row wrap" style={{ marginBlockEnd: 'var(--s-4)' }}>
        <Pill>{dateTime(String(sale.created_at))}</Pill>
        <Pill>{String(sale.user_name ?? '')}</Pill>
        {String(sale.status) === 'voided' ? <Pill kind="danger">{t('sales.voided')}: {String(sale.void_reason ?? '')}</Pill> : null}
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>{t('common.product')}</th>
              <th className="num">{t('common.qty')}</th>
              <th className="num">{t('common.price')}</th>
              <th className="num">{t('common.total')}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={String(l.id)}>
                <td>
                  <div className="strong">{lang === 'ar' ? String(l.name_ar) : String(l.name_en)}</div>
                  <div className="xsmall muted">
                    {String(l.size)} · {lang === 'ar' ? String(l.color_ar ?? '') : String(l.color_en ?? '')}
                    {Number(l.returned_qty) > 0 ? ` · ${t('exchange.alreadyExchanged')} ${Number(l.returned_qty)}` : ''}
                  </div>
                </td>
                <td className="num tnum">{Number(l.qty)}</td>
                <td className="num tnum">{money(Number(l.unit_price))}</td>
                <td className="num tnum strong">{money(Number(l.net))}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3}>{t('common.total')}</td>
              <td className="num tnum">{money(Number(sale.payable))}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="col" style={{ marginBlockStart: 'var(--s-4)', gap: 'var(--s-1)' }}>
        {payments.map((p) => (
          <div className="row" key={String(p.id)}>
            <span className="small">{String(p.method)} {String(p.currency)}</span>
            <div className="spacer" />
            <span className="tnum small">{money(Number(p.amount_ils))}</span>
          </div>
        ))}
      </div>

      <div className="center" style={{ marginBlockStart: 'var(--s-5)', background: '#fff', padding: 'var(--s-3)' }}>
        <Barcode value={String(sale.receipt_no)} height={36} />
      </div>

      {voiding ? (
        <Modal
          title={t('sales.void')}
          onClose={() => setVoiding(false)}
          footer={
            <>
              <Button onClick={() => setVoiding(false)}>{t('common.cancel')}</Button>
              <div className="spacer" />
              <Button
                variant="danger"
                disabled={!reason.trim()}
                onClick={() => {
                  void api(`/sales/${id}/void`, { body: { reason } })
                    .then(() => {
                      toast.ok(t('sales.voidedOk'));
                      onChanged();
                    })
                    .catch((e: Error) => toast.err(e.message));
                }}
              >
                {t('sales.void')}
              </Button>
            </>
          }
        >
          <p className="small muted">{t('sales.voidWarn')}</p>
          <Field label={t('sales.voidReason')}>
            <input value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
          </Field>
        </Modal>
      ) : null}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Customers
// ---------------------------------------------------------------------------

export function Customers() {
  const t = useT();
  const { can } = useAuth();
  const { settings, money } = useSettings();
  const toast = useToast();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api<Record<string, unknown>[]>(`/exchange/customers?q=${encodeURIComponent(q)}`));
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    const timer = setTimeout(() => void load(), 200);
    return () => clearTimeout(timer);
  }, [load]);

  return (
    <div className="col">
      <div className="row wrap">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('common.searchPlaceholder')} aria-label={t('common.searchPlaceholder')} style={{ maxWidth: 360 }} />
        <div className="spacer" />
        {can('customer.edit') ? (
          <Button variant="primary" onClick={() => setEditing({})}>{t('customers.new')}</Button>
        ) : null}
      </div>

      {loading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty icon="👥" title={t('customers.empty')} hint={t('customers.emptyHint')} />
      ) : (
        <div className="card flush">
          <div className="table-wrap">
            <table className="cards">
              <thead>
                <tr>
                  <th>{t('common.name')}</th>
                  <th>{t('common.phone')}</th>
                  <th className="num">{t('customers.purchases')}</th>
                  <th className="num">{t('customers.creditBalance')}</th>
                  {settings.customerTabsEnabled ? <th className="num">{t('customers.tab')}</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={String(r.id)} style={{ cursor: 'pointer' }} onClick={() => setOpen(String(r.id))}>
                    <td className="strong" data-label={t('common.name')}>{String(r.name)}</td>
                    <td className="tnum" data-label={t('common.phone')}>{String(r.phone ?? '')}</td>
                    <td className="num" data-label={t('customers.purchases')}>{Number(r.purchases)}</td>
                    <td className="num tnum" data-label={t('customers.creditBalance')}>{money(Number(r.credit))}</td>
                    {settings.customerTabsEnabled ? (
                      <td className="num tnum" data-label={t('customers.tab')}>{money(Number(r.tab_balance))}</td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing ? (
        <Modal
          title={t('customers.new')}
          onClose={() => setEditing(null)}
          footer={
            <>
              <Button onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
              <div className="spacer" />
              <Button
                variant="primary"
                onClick={() => {
                  void api('/exchange/customers', { body: editing }).then(() => {
                    toast.ok(t('common.saved'));
                    setEditing(null);
                    void load();
                  });
                }}
              >
                {t('common.save')}
              </Button>
            </>
          }
        >
          <Field label={t('common.name')}>
            <input
              value={String(editing.name ?? '')}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              autoFocus
            />
          </Field>
          <Field label={t('common.phone')}>
            <input
              type="tel"
              dir="ltr"
              value={String(editing.phone ?? '')}
              onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
            />
          </Field>
          <Field label={t('common.notes')}>
            <textarea
              value={String(editing.notes ?? '')}
              onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
            />
          </Field>
        </Modal>
      ) : null}

      {open ? <CustomerDetail id={open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

function CustomerDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const t = useT();
  const { money, date, settings } = useSettings();
  const { can } = useAuth();
  const [payingTab, setPayingTab] = useState(false);
  const [data, setData] = useState<{
    customer: Record<string, unknown>;
    sales: Record<string, unknown>[];
    credits: Record<string, unknown>[];
  } | null>(null);

  const load = useCallback(() => {
    void api<typeof data>(`/exchange/customers/${id}`).then(setData).catch(() => undefined);
  }, [id]);

  useEffect(() => load(), [load]);

  if (!data) return <Modal title={t('common.loading')} onClose={onClose}><Loading /></Modal>;

  return (
    <Modal title={String(data.customer.name)} wide onClose={onClose}>
      <div className="row wrap" style={{ marginBlockEnd: 'var(--s-4)' }}>
        <Pill>{String(data.customer.phone ?? '')}</Pill>
        <Pill kind="accent">
          {t('customers.creditBalance')}:{' '}
          {money(data.credits.reduce((a, c) => a + Number(c.remaining), 0))}
        </Pill>
        {settings.customerTabsEnabled && Number(data.customer.tab_balance) > 0 ? (
          <>
            <Pill kind="warn">
              {t('tab.owes')}: {money(Number(data.customer.tab_balance))}
            </Pill>
            {can('customer.tab') ? (
              <Button size="sm" variant="primary" onClick={() => setPayingTab(true)}>
                {t('tab.takePayment')}
              </Button>
            ) : null}
          </>
        ) : null}
      </div>

      {payingTab ? (
        <TakePayment
          customer={{
            id: String(data.customer.id),
            name: String(data.customer.name),
            balance: Number(data.customer.tab_balance),
          }}
          onClose={() => setPayingTab(false)}
          onPaid={() => {
            setPayingTab(false);
            load();
          }}
        />
      ) : null}

      <h3>{t('customers.purchases')}</h3>
      <div className="table-wrap">
        <table className="cards">
          <tbody>
            {data.sales.map((s) => (
              <tr key={String(s.id)}>
                <td className="strong">{String(s.receipt_no)}</td>
                <td>{date(String(s.created_at))}</td>
                <td className="num">{Number(s.item_count)}</td>
                <td className="num tnum">{money(Number(s.total))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.credits.length > 0 ? (
        <>
          <h3 style={{ marginBlockStart: 'var(--s-5)' }}>{t('credits.title')}</h3>
          <div className="table-wrap">
            <table className="cards">
              <tbody>
                {data.credits.map((c) => (
                  <tr key={String(c.id)}>
                    <td className="strong tnum">{String(c.code)}</td>
                    <td className="num tnum">{money(Number(c.issued))}</td>
                    <td className="num tnum">{money(Number(c.remaining))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Store credit
// ---------------------------------------------------------------------------

export function Credits() {
  const t = useT();
  const { can } = useAuth();
  const { settings, money, date } = useSettings();
  const toast = useToast();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(false);
  const [amount, setAmount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api<Record<string, unknown>[]>(`/exchange/credits?q=${encodeURIComponent(q)}`));
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    const timer = setTimeout(() => void load(), 200);
    return () => clearTimeout(timer);
  }, [load]);

  const printVoucher = (r: Record<string, unknown>) => {
    void printDocument(
      `Voucher ${r.code}`,
      `<div style="text-align:center;border:2px dashed #000;padding:14mm;max-width:110mm;margin:0 auto">
         <div style="font-size:13pt;font-weight:700">${escapeHtml(settings.storeNameAr)}</div>
         <div style="font-size:11pt">${escapeHtml(settings.storeNameEn)}</div>
         <div style="margin:6mm 0;font-size:10pt">${escapeHtml(t('credits.title'))}</div>
         <div style="font-size:26pt;font-weight:700">${escapeHtml(money(Number(r.remaining)))}</div>
         <div style="margin:6mm 0">${barcodeSvg(String(r.code), { height: 40, width: 1.6 })}</div>
         ${r.expires_at ? `<div style="font-size:9pt">${escapeHtml(t('credits.expires'))}: ${escapeHtml(date(String(r.expires_at)))}</div>` : ''}
         <div style="font-size:9pt;margin-top:4mm">${escapeHtml(settings.address)} · ${escapeHtml(settings.phone)}</div>
       </div>`,
    );
  };

  return (
    <div className="col">
      <div className="row wrap">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('credits.code')} aria-label={t('credits.code')}
          style={{ maxWidth: 360 }}
          data-scan-target="true"
        />
        <div className="spacer" />
        {can('credit.issue') ? (
          <Button variant="primary" onClick={() => setIssuing(true)}>{t('credits.new')}</Button>
        ) : null}
      </div>

      {loading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty icon="🎟️" title={t('credits.empty')} />
      ) : (
        <div className="card flush">
          <div className="table-wrap">
            <table className="cards">
              <thead>
                <tr>
                  <th>{t('credits.code')}</th>
                  <th>{t('common.customer')}</th>
                  <th>{t('common.date')}</th>
                  <th className="num">{t('credits.issued')}</th>
                  <th className="num">{t('credits.remaining')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const expired = r.expires_at && new Date(String(r.expires_at)) < new Date();
                  return (
                    <tr key={String(r.id)}>
                      <td className="strong tnum" data-label={t('credits.code')}>{String(r.code)}</td>
                      <td data-label={t('common.customer')}>{String(r.customer_name ?? '')}</td>
                      <td data-label={t('common.date')}>{date(String(r.created_at))}</td>
                      <td className="num tnum" data-label={t('credits.issued')}>{money(Number(r.issued))}</td>
                      <td className="num" data-label={t('credits.remaining')}>
                        <Pill kind={Number(r.remaining) > 0 && !expired ? 'ok' : 'warn'}>
                          {expired ? t('credits.expired') : money(Number(r.remaining))}
                        </Pill>
                      </td>
                      <td className="num">
                        <Button size="sm" onClick={() => printVoucher(r)}>{t('credits.printVoucher')}</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {issuing ? (
        <Modal
          title={t('credits.new')}
          onClose={() => setIssuing(false)}
          footer={
            <>
              <Button onClick={() => setIssuing(false)}>{t('common.cancel')}</Button>
              <div className="spacer" />
              <Button
                variant="primary"
                disabled={amount <= 0}
                onClick={() => {
                  void api('/exchange/credits', { body: { amount } }).then(() => {
                    toast.ok(t('common.saved'));
                    setIssuing(false);
                    setAmount(0);
                    void load();
                  });
                }}
              >
                {t('credits.new')}
              </Button>
            </>
          }
        >
          <Field label={t('common.amount')}>
            <MoneyInput className="big" value={amount} onValue={setAmount} autoFocus />
          </Field>
        </Modal>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Expenses
// ---------------------------------------------------------------------------

export function Expenses() {
  const t = useT();
  const { can } = useAuth();
  const { money, date } = useSettings();
  const toast = useToast();
  const { range, setRange } = useDateRange('month');
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [form, setForm] = useState({ category: 'Rent', amount: 0, vatAmount: 0, note: '', spentOn: dayKey(new Date()) });

  const categories = [
    { key: 'Rent', label: t('expenses.rent') },
    { key: 'Salaries', label: t('expenses.salaries') },
    { key: 'Electricity', label: t('expenses.electricity') },
    { key: 'Water', label: t('expenses.water') },
    { key: 'Cleaning', label: t('expenses.cleaning') },
    { key: 'Bags and packaging', label: t('expenses.packaging') },
    { key: 'Transport', label: t('expenses.transport') },
    { key: 'Other', label: t('expenses.other') },
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api<Record<string, unknown>[]>(`/stock/expenses?from=${range.from}&to=${range.to}`));
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    void load();
  }, [load]);

  const total = rows.reduce((a, r) => a + Number(r.amount), 0);

  return (
    <div className="col">
      <div className="row wrap">
        <RangePicker range={range} onChange={setRange} />
        <div className="spacer" />
        {can('expense.edit') ? (
          <Button variant="primary" onClick={() => setAdding(true)}>{t('expenses.new')}</Button>
        ) : null}
      </div>

      {loading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty icon="🧮" title={t('expenses.empty')} />
      ) : (
        <div className="card flush">
          <div className="table-wrap">
            <table className="cards">
              <thead>
                <tr>
                  <th>{t('common.date')}</th>
                  <th>{t('expenses.category')}</th>
                  <th>{t('common.notes')}</th>
                  <th className="num">{t('common.vat')}</th>
                  <th className="num">{t('common.amount')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={String(r.id)}>
                    <td data-label={t('common.date')}>{date(String(r.spent_on))}</td>
                    <td className="strong" data-label={t('expenses.category')}>
                      {categories.find((c) => c.key === r.category)?.label ?? String(r.category)}
                    </td>
                    <td data-label={t('common.notes')}>{String(r.note ?? '')}</td>
                    <td className="num tnum" data-label={t('common.vat')}>{money(Number(r.vat_amount))}</td>
                    <td className="num tnum strong" data-label={t('common.amount')}>{money(Number(r.amount))}</td>
                    <td className="num">
                      <Button size="sm" variant="ghost" aria-label={t('common.delete')} onClick={() => setDeleting(String(r.id))}>✕</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4}>{t('common.total')}</td>
                  <td className="num tnum">{money(total)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {adding ? (
        <Modal
          title={t('expenses.new')}
          onClose={() => setAdding(false)}
          footer={
            <>
              <Button onClick={() => setAdding(false)}>{t('common.cancel')}</Button>
              <div className="spacer" />
              <Button
                variant="primary"
                disabled={form.amount <= 0}
                onClick={() => {
                  void api('/stock/expenses', { body: form }).then(() => {
                    toast.ok(t('common.saved'));
                    setAdding(false);
                    setForm({ ...form, amount: 0, vatAmount: 0, note: '' });
                    void load();
                  });
                }}
              >
                {t('common.save')}
              </Button>
            </>
          }
        >
          <Field label={t('expenses.category')}>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {categories.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </Field>
          <Field label={t('common.amount')}>
            <MoneyInput className="big" value={form.amount} onValue={(v) => setForm({ ...form, amount: v })} />
          </Field>
          <Field label={t('expenses.vatOnIt')} hint={t('reports.expenseVat')}>
            <MoneyInput value={form.vatAmount} onValue={(v) => setForm({ ...form, vatAmount: v })} />
          </Field>
          <Field label={t('expenses.spentOn')}>
            <input type="date" value={form.spentOn} onChange={(e) => setForm({ ...form, spentOn: e.target.value })} />
          </Field>
          <Field label={t('common.notes')}>
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </Field>
        </Modal>
      ) : null}

      {deleting ? (
        <Confirm
          title={t('common.delete')}
          message={t('common.confirm')}
          confirmLabel={t('common.delete')}
          danger
          onConfirm={() => {
            void api(`/stock/expenses/${deleting}`, { method: 'DELETE' }).then(load);
            setDeleting(null);
          }}
          onCancel={() => setDeleting(null)}
        />
      ) : null}
    </div>
  );
}
